/** Looking up Pokémon and moves in a data snapshot */
import { candidateIds, toID } from './names.js';
import { SpeciesFlag, type MoveInfo, type PokemonDataSnapshot, type Species } from './types.js';

const CATEGORIES = { P: 'Physical', S: 'Special', X: 'Status' } as const;

export interface PokemonData {
	snapshot: PokemonDataSnapshot;
	types: string[];
	/** Finds a Pokémon by id or name (including spreadsheet-style names); null if there's no such Pokémon */
	getSpecies(nameOrId: string): Species | null;
	/** Finds a move by id or name; null if there's no such move */
	getMove(nameOrId: string): MoveInfo | null;
	/** The ids of every move a Pokémon can learn */
	getLearnset(speciesId: string): ReadonlySet<string>;
	allSpeciesIds(): string[];
}

export function createData(snapshot: PokemonDataSnapshot): PokemonData {
	const speciesByName = new Map<string, string>();
	for (const [id, row] of Object.entries(snapshot.species)) speciesByName.set(toID(row[0]), id);
	const movesByName = new Map<string, string>();
	for (const [id, row] of Object.entries(snapshot.moves)) movesByName.set(toID(row[0]), id);
	const learnsetCache = new Map<string, ReadonlySet<string>>();

	function getSpecies(nameOrId: string): Species | null {
		for (const candidate of candidateIds(nameOrId)) {
			const id = snapshot.species[candidate] ? candidate : speciesByName.get(candidate);
			const row = id ? snapshot.species[id] : undefined;
			if (!id || !row) continue;
			const [name, types, stats, abilities, sprite, , flags = 0, baseSpecies = '', changesFrom = ''] = row;
			return {
				id, name, types, abilities, sprite, nfe: !!(flags & SpeciesFlag.nfe), baseSpecies: baseSpecies || id,
				needsItem: !!(flags & SpeciesFlag.needsItem), mega: !!(flags & SpeciesFlag.mega), changesFrom,
				stats: { hp: stats[0], atk: stats[1], def: stats[2], spa: stats[3], spd: stats[4], spe: stats[5] },
				bst: stats.reduce((total, stat) => total + stat, 0),
			};
		}
		return null;
	}

	function getMove(nameOrId: string): MoveInfo | null {
		const raw = toID(nameOrId);
		const id = snapshot.moves[raw] ? raw : movesByName.get(raw);
		const row = id ? snapshot.moves[id] : undefined;
		if (!id || !row) return null;
		const [name, type, category, basePower, accuracy, priority, spread, power, traits] = row;
		return {
			id, name, type, basePower, priority, spread: !!spread, power: power ?? basePower, traits: traits ?? 0,
			category: CATEGORIES[category], accuracy: accuracy === 101 ? true : accuracy,
		};
	}

	function getLearnset(speciesId: string): ReadonlySet<string> {
		const row = snapshot.species[speciesId];
		if (!row) return new Set();
		const key = row[5] || speciesId;
		let learnset = learnsetCache.get(key);
		if (!learnset) {
			const text = snapshot.learnsets[key] || '';
			learnset = new Set(text ? text.split(',').map(index => snapshot.moveIds[parseInt(index, 36)]!) : []);
			learnsetCache.set(key, learnset);
		}
		return learnset;
	}

	return {
		snapshot, types: snapshot.types, getSpecies, getMove, getLearnset,
		allSpeciesIds: () => Object.keys(snapshot.species),
	};
}
